// AnalyticsTab.tsx - Comprehensive admin analytics dashboard with real-time metrics
import React, { useState, useEffect } from 'react';
import { UsageDashboard } from '../subscription';
import {
  fetchOverallUsageMetrics,
  fetchTokenAnalytics,
  fetchAbuseAnalytics,
  formatNumber,
  formatDate,
  formatRelativeTime
} from '../../services/analytics';
import type { OverallUsageMetrics, TokenAnalytics, AbuseAnalytics } from '../../types/analytics';

type ViewMode = 'overview' | 'tokens' | 'abuse' | 'user';

const AnalyticsTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userIdInput, setUserIdInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [overallMetrics, setOverallMetrics] = useState<OverallUsageMetrics | null>(null);
  const [tokenAnalytics, setTokenAnalytics] = useState<TokenAnalytics | null>(null);
  const [abuseAnalytics, setAbuseAnalytics] = useState<AbuseAnalytics | null>(null);
  
  // Loading states
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [loadingAbuse, setLoadingAbuse] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState<number>(7);

  // Load initial data
  useEffect(() => {
    loadAllData();
  }, [selectedPeriod]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadAllData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedPeriod]);

  const loadAllData = async (silent: boolean = false) => {
    if (!silent) {
      setLoadingOverview(true);
      setLoadingTokens(true);
      setLoadingAbuse(true);
    }

    try {
      const [overview, tokens, abuse] = await Promise.all([
        fetchOverallUsageMetrics(selectedPeriod),
        fetchTokenAnalytics(selectedPeriod),
        fetchAbuseAnalytics(selectedPeriod)
      ]);

      setOverallMetrics(overview);
      setTokenAnalytics(tokens);
      setAbuseAnalytics(abuse);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      if (!silent) {
        setLoadingOverview(false);
        setLoadingTokens(false);
        setLoadingAbuse(false);
      }
    }
  };

  const handleViewUserAnalytics = () => {
    if (!userIdInput.trim()) {
      setError('Please enter a user ID');
      return;
    }

    localStorage.setItem('userId', userIdInput.trim());
    setSelectedUserId(userIdInput.trim());
    setViewMode('user');
    setError(null);
  };

  const handleCloseDashboard = () => {
    setSelectedUserId(null);
    setUserIdInput('');
    setViewMode('overview');
  };

  // Render user-specific dashboard
  if (selectedUserId && viewMode === 'user') {
    return <UsageDashboard onClose={handleCloseDashboard} />;
  }

  return (
    <div className="h-full flex flex-col space-y-6 overflow-y-auto">
      {/* Header with controls */}
      <div className="bg-slate-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Real-Time Analytics Dashboard</h2>
            <p className="text-slate-400 text-sm">
              Updated {formatRelativeTime(lastUpdated.toISOString())}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded border transition-colors ${
                autoRefresh
                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                  : 'bg-slate-700 border-slate-600 text-slate-400'
              }`}
              title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={() => loadAllData()}
              className="px-3 py-2 bg-slate-700 text-white rounded border border-slate-600 hover:bg-slate-600 transition-colors"
              title="Refresh now"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              className="px-3 py-2 bg-slate-700 text-white rounded border border-slate-600 focus:outline-none focus:border-emerald-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'overview'
                ? 'bg-emerald-500 text-slate-900 font-medium'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('tokens')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'tokens'
                ? 'bg-emerald-500 text-slate-900 font-medium'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Token Analytics
          </button>
          <button
            onClick={() => setViewMode('abuse')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'abuse'
                ? 'bg-emerald-500 text-slate-900 font-medium'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Abuse Detection
          </button>
          <button
            onClick={() => setViewMode('user')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'user'
                ? 'bg-emerald-500 text-slate-900 font-medium'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            User Analytics
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Overview View */}
      {viewMode === 'overview' && overallMetrics && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Total Users</p>
              <p className="text-3xl font-bold text-white">
                {formatNumber(overallMetrics.overview.totalUsers)}
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Active Users (24h)</p>
              <p className="text-3xl font-bold text-emerald-400">
                {formatNumber(overallMetrics.overview.activeUsers24h)}
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Messages (24h)</p>
              <p className="text-3xl font-bold text-blue-400">
                {formatNumber(overallMetrics.overview.messages24h)}
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Tokens (24h)</p>
              <p className="text-3xl font-bold text-purple-400">
                {formatNumber(overallMetrics.overview.tokens24h)}
              </p>
            </div>
          </div>

          {/* Tier Distribution */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">User Distribution by Tier</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {overallMetrics.tierDistribution.map((tier) => (
                <div key={tier.tier} className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1 capitalize">{tier.tier} Tier</p>
                  <p className="text-2xl font-bold text-white">
                    {formatNumber(tier.user_count)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Users */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Top Users by Token Usage</h3>
            <div className="space-y-2">
              {overallMetrics.topUsers.slice(0, 10).map((user, index) => (
                <div key={user.user_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 font-mono text-sm w-6">{index + 1}</span>
                    <span className="text-white font-mono text-xs">{user.user_id}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-emerald-400">
                      {formatNumber(user.total_messages)} msgs
                    </span>
                    <span className="text-blue-400">
                      {formatNumber(user.total_tokens)} tokens
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Daily Stats */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Daily Activity Trends</h3>
            <div className="space-y-2">
              {overallMetrics.dailyStats.slice(0, 14).map((day) => {
                const maxMessages = Math.max(...overallMetrics.dailyStats.map(d => d.total_messages), 1);
                const percentage = (day.total_messages / maxMessages) * 100;
                
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-slate-400 text-sm w-24 flex-shrink-0">
                      {formatDate(day.date)}
                    </span>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="flex-1 bg-slate-700 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-300 flex items-center justify-end pr-2"
                          style={{ width: `${percentage}%` }}
                        >
                          {day.total_messages > 0 && (
                            <span className="text-xs text-white font-semibold">
                              {formatNumber(day.total_messages)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-slate-400 text-sm w-32 text-right">
                        {formatNumber(day.active_users)} users
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Token Analytics View */}
      {viewMode === 'tokens' && tokenAnalytics && (
        <div className="space-y-6">
          {/* Token Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Total Tokens (Period)</p>
              <p className="text-3xl font-bold text-purple-400">
                {formatNumber(tokenAnalytics.trends.totalPeriod)}
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Growth Rate</p>
              <p className={`text-3xl font-bold ${
                tokenAnalytics.trends.growthRate >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {tokenAnalytics.trends.growthRate >= 0 ? '+' : ''}{tokenAnalytics.trends.growthRate}%
              </p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Avg Tokens/Message</p>
              <p className="text-3xl font-bold text-blue-400">
                {formatNumber(tokenAnalytics.averages.tokensPerMessage)}
              </p>
            </div>
          </div>

          {/* Token Breakdown */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Token Breakdown</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Prompt Tokens</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {formatNumber(tokenAnalytics.averages.promptTokens)}
                </p>
                <p className="text-xs text-slate-500">avg per message</p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Completion Tokens</p>
                <p className="text-2xl font-bold text-blue-400">
                  {formatNumber(tokenAnalytics.averages.completionTokens)}
                </p>
                <p className="text-xs text-slate-500">avg per message</p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Total Tokens</p>
                <p className="text-2xl font-bold text-purple-400">
                  {formatNumber(tokenAnalytics.averages.tokensPerMessage)}
                </p>
                <p className="text-xs text-slate-500">avg per message</p>
              </div>
            </div>
          </div>

          {/* Usage by Tier */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Token Usage by Tier</h3>
            <div className="space-y-3">
              {tokenAnalytics.byTier.map((tier) => (
                <div key={tier.tier} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium capitalize">{tier.tier} Tier</p>
                    <p className="text-slate-400 text-sm">
                      {formatNumber(tier.user_count)} users
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-purple-400 font-bold">
                      {formatNumber(tier.total_tokens)} tokens
                    </p>
                    <p className="text-slate-400 text-sm">
                      {formatNumber(Math.round(tier.avg_tokens_per_user))} avg/user
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Peak Usage Times */}
          <div className="bg-slate-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Peak Usage Hours</h3>
            <div className="flex items-end gap-1 h-48">
              {tokenAnalytics.peakUsage.map((hour) => {
                const maxTokens = Math.max(...tokenAnalytics.peakUsage.map(h => Number(h.total_tokens)), 1);
                const height = (Number(hour.total_tokens) / maxTokens) * 100;
                
                return (
                  <div key={hour.hour} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-purple-500 rounded-t transition-all duration-300 hover:bg-purple-400 relative group"
                      style={{ height: `${height}%` }}
                    >
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                        {hour.hour}:00 - {formatNumber(Number(hour.total_tokens))} tokens
                      </div>
                    </div>
                    <span className="text-xs text-slate-500 mt-1">{hour.hour}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Abuse Detection View */}
      {viewMode === 'abuse' && abuseAnalytics && (
        <div className="space-y-6">
          {/* Abuse Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Rate Limit Violations</p>
              <p className="text-3xl font-bold text-red-400">
                {abuseAnalytics.rateLimitViolations.count}
              </p>
              <p className="text-xs text-slate-500 mt-1">today</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">High Volume Users</p>
              <p className="text-3xl font-bold text-yellow-400">
                {abuseAnalytics.highVolumeUsers.count}
              </p>
              <p className="text-xs text-slate-500 mt-1">last 7 days</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <p className="text-slate-400 text-sm mb-1">Unusual Spikes</p>
              <p className="text-3xl font-bold text-orange-400">
                {abuseAnalytics.unusualPatterns.count}
              </p>
              <p className="text-xs text-slate-500 mt-1">detected</p>
            </div>
          </div>

          {/* Rate Limit Violations */}
          {abuseAnalytics.rateLimitViolations.today.length > 0 && (
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Rate Limit Violations (Today)</h3>
              <div className="space-y-2">
                {abuseAnalytics.rateLimitViolations.today.map((violation) => (
                  <div key={violation.user_id} className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div>
                      <p className="text-white font-mono text-xs">{violation.user_id}</p>
                      <p className="text-slate-400 text-xs capitalize">{violation.tier} tier</p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-400 font-bold">
                        {formatNumber(violation.message_count)} messages
                      </p>
                      <p className="text-slate-400 text-sm">
                        {formatNumber(violation.token_count)} tokens
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* High Volume Users */}
          {abuseAnalytics.highVolumeUsers.users.length > 0 && (
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">High Volume Users (7 Days)</h3>
              <div className="space-y-2">
                {abuseAnalytics.highVolumeUsers.users.map((user) => (
                  <div key={user.user_id} className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div>
                      <p className="text-white font-mono text-xs">{user.user_id}</p>
                      <p className="text-slate-400 text-xs">
                        Active {user.active_days} days • {user.tier} tier
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400 font-bold">
                        {formatNumber(user.total_messages)} messages
                      </p>
                      <p className="text-slate-400 text-sm">
                        {formatNumber(user.total_tokens)} tokens
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unusual Patterns */}
          {abuseAnalytics.unusualPatterns.spikes.length > 0 && (
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Unusual Activity Spikes</h3>
              <div className="space-y-2">
                {abuseAnalytics.unusualPatterns.spikes.map((spike) => (
                  <div key={`${spike.user_id}-${spike.date}`} className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div>
                      <p className="text-white font-mono text-xs">{spike.user_id}</p>
                      <p className="text-slate-400 text-xs">{formatDate(spike.date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-orange-400 font-bold">
                        {formatNumber(spike.message_count)} messages
                      </p>
                      <p className="text-slate-400 text-sm">
                        {formatNumber(spike.token_count)} tokens
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Analytics View */}
      {viewMode === 'user' && !selectedUserId && (
        <div className="bg-slate-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">User Usage Analytics</h2>
          <p className="text-slate-400 mb-6">
            View detailed usage analytics for any user by entering their user ID.
          </p>

          <div className="flex gap-3 max-w-2xl">
            <input
              type="text"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleViewUserAnalytics()}
              placeholder="Enter user ID..."
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded border border-slate-600 focus:outline-none focus:border-emerald-500"
            />
            <button
              onClick={handleViewUserAnalytics}
              className="px-6 py-2 bg-emerald-500 text-slate-900 rounded hover:bg-emerald-400 transition-colors font-medium"
            >
              View Analytics
            </button>
          </div>

          <div className="mt-6 p-4 bg-slate-700/50 rounded border border-slate-600">
            <h3 className="text-lg font-semibold text-white mb-2">How to find a user ID:</h3>
            <ol className="list-decimal list-inside text-slate-300 space-y-1">
              <li>Go to the User Management tab</li>
              <li>Find the user you want to view analytics for</li>
              <li>Copy their user ID from the user list</li>
              <li>Paste it in the field above and click "View Analytics"</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
